import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { CREDENTIAL_ACTIVITY_SCOPES } from "@njbsoft/api-client";
import {
  useCreateMemberCredential,
  useUpdateFieldCredential,
  type FieldCredential,
  type FieldCredentialCreate,
  type CredentialActivityScope,
} from "@/hooks/use-field-credentials";
import { useToast } from "@/hooks/use-toast";
import { Button, FormField, inputClass, Select } from "@/components/ui";

export interface CredentialHolder {
  /** The org member's PROFILE id — the create POST is member-keyed, and the
   * utility writes credentials only for its own staff (a tester's rows are
   * theirs and their company's). Edits are holder-independent (the PATCH is
   * credential-keyed). */
  id: string;
  name: string;
}

export interface CredentialFormProps {
  /**
   * Fixed create holder — every create entry point holds a known person
   * (the member-detail "Add credential" entry). Ignored on edit: the
   * credential's own holder renders there.
   */
  fixedHolder: CredentialHolder;
  /** When present the form edits this credential instead of creating one. */
  credential?: FieldCredential | null;
  /**
   * Called after the mutation succeeds with the persisted row. Toasts and
   * cache invalidation are owned by the form / hooks regardless; this is a
   * caller-side hook only.
   */
  onSuccess?: (credential?: FieldCredential) => void;
  /** Fired when the inline Cancel button (hidden under `hideActions`) is clicked. */
  onCancel?: () => void;
  /** Suppress the inline Cancel/Save row — a container (the credential
   * drawer) drives `submit()` from its own footer. Defaults to `false`. */
  hideActions?: boolean;
  /** Fired whenever the active mutation's pending state flips, so a container
   * rendering its own affordance can gate on an in-flight submit. */
  onSubmittingChange?: (submitting: boolean) => void;
}

export interface CredentialFormHandle {
  /** Programmatically triggers the form's validated submit — the credential
   * drawer's footer Save drives this. Resolves with the persisted row on
   * success (which ALSO fires `onSuccess`), or `null` when client-side
   * validation blocked the submit (nothing was sent; the inline field errors
   * are already surfaced) or the request failed. */
  submit: () => Promise<FieldCredential | null>;
}

interface FormValues {
  activity_scope: CredentialActivityScope | "";
  certifying_body: string;
  identification_number: string;
  issued_at: string;
  expires_at: string;
  notes: string;
}

type ErrorSlot = keyof FormValues;

function emptyValues(): FormValues {
  return {
    activity_scope: "",
    certifying_body: "",
    identification_number: "",
    issued_at: "",
    expires_at: "",
    notes: "",
  };
}

function fromCredential(c: FieldCredential): FormValues {
  return {
    activity_scope: c.activity_scope,
    certifying_body: c.certifying_body,
    identification_number: c.identification_number,
    issued_at: c.issued_at ?? "",
    expires_at: c.expires_at,
    notes: c.notes ?? "",
  };
}

/**
 * Add / edit form for person-keyed credential evidence per AWWA M14 §10
 * per-purveyor verification. Create routes to the member-keyed POST for the
 * fixed holder; edit uses the credential-keyed PATCH with the holder
 * rendered read-only.
 *
 * Exposes imperative `submit()` via forwardRef (see
 * `CredentialFormHandle`), distinguishing "blocked by validation, nothing
 * sent" from "sent, pending" via `attemptedRef` — flipped synchronously only
 * when a real request is dispatched, mirroring the canonical
 * `TestKitCreatePanel` mechanics.
 */
export const CredentialForm = forwardRef<CredentialFormHandle, CredentialFormProps>(
  function CredentialForm(
    { fixedHolder, credential, onSuccess, onCancel, hideActions, onSubmittingChange },
    ref,
  ) {
    const { t } = useTranslation(["field-credentials", "common"]);
    const toasts = useToast();
    const isEdit = Boolean(credential);

    const createMemberMutation = useCreateMemberCredential(fixedHolder.id);
    const updateMutation = useUpdateFieldCredential(credential?.id ?? "");

    const [values, setValues] = useState<FormValues>(
      credential ? fromCredential(credential) : emptyValues(),
    );
    const [errors, setErrors] = useState<Partial<Record<ErrorSlot, string>>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);

    const formRef = useRef<HTMLFormElement>(null);
    const attemptedRef = useRef(false);
    const pendingResolveRef = useRef<((row: FieldCredential | null) => void) | null>(null);

    const isPending = createMemberMutation.isPending || updateMutation.isPending;

    function update<K extends keyof FormValues>(key: K, value: FormValues[K]) {
      setValues((prev) => ({ ...prev, [key]: value }));
    }

    function scrollToFirstError(field: ErrorSlot) {
      const el = formRef.current?.querySelector<HTMLElement>(`[data-field="${field}"]`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }

    const handleSubmit = (e: FormEvent) => {
      e.preventDefault();
      setSubmitError(null);
      const nextErrors: Partial<Record<ErrorSlot, string>> = {};
      if (values.activity_scope === "") nextErrors.activity_scope = t("form.scopeRequired");
      if (values.certifying_body.trim() === "")
        nextErrors.certifying_body = t("form.certifyingBodyRequired");
      if (values.identification_number.trim() === "")
        nextErrors.identification_number = t("form.identificationNumberRequired");
      if (values.expires_at === "") nextErrors.expires_at = t("form.expiresAtRequired");
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0 || values.activity_scope === "") {
        const firstError = (
          ["activity_scope", "certifying_body", "identification_number", "expires_at"] as const
        ).find((k) => nextErrors[k]);
        if (firstError) scrollToFirstError(firstError);
        return;
      }

      const callbacks = {
        onSuccess: (row?: FieldCredential) => {
          toasts.success(
            isEdit
              ? "field-credentials:form.updateSuccess"
              : "field-credentials:form.createSuccess",
          );
          onSuccess?.(row);
          pendingResolveRef.current?.(row ?? null);
          pendingResolveRef.current = null;
        },
        onError: () => {
          setSubmitError(isEdit ? t("errors.updateFailed") : t("errors.createFailed"));
          pendingResolveRef.current?.(null);
          pendingResolveRef.current = null;
        },
      };

      attemptedRef.current = true;

      if (isEdit && credential) {
        updateMutation.mutate(
          {
            activity_scope: values.activity_scope,
            certifying_body: values.certifying_body.trim(),
            identification_number: values.identification_number.trim(),
            issued_at: values.issued_at === "" ? null : values.issued_at,
            expires_at: values.expires_at,
            notes: values.notes.trim() === "" ? null : values.notes.trim(),
          },
          callbacks,
        );
        return;
      }

      const body: FieldCredentialCreate = {
        activity_scope: values.activity_scope,
        certifying_body: values.certifying_body.trim(),
        identification_number: values.identification_number.trim(),
        issued_at: values.issued_at === "" ? null : values.issued_at,
        expires_at: values.expires_at,
        notes: values.notes.trim() === "" ? null : values.notes.trim(),
      };
      createMemberMutation.mutate(body, callbacks);
    };

    useImperativeHandle(ref, () => ({
      submit: () =>
        new Promise<FieldCredential | null>((resolve) => {
          if (!formRef.current) {
            resolve(null);
            return;
          }
          attemptedRef.current = false;
          pendingResolveRef.current = resolve;
          formRef.current.requestSubmit();
          if (!attemptedRef.current) {
            // Validation blocked the submit — nothing was sent, resolve now.
            pendingResolveRef.current = null;
            resolve(null);
          }
          // else: wait for the mutate callbacks to resolve via
          // pendingResolveRef — correct whether the mutation settles
          // synchronously (tests) or asynchronously (the real network call).
        }),
    }));

    // Unmount safety net: React Query drops the per-`mutate` callbacks when
    // the owning component unmounts, so a `submit()` promise still waiting on
    // pendingResolveRef would hang forever (e.g. a container closes the
    // drawer during a slow submit). Settle it with null.
    useEffect(
      () => () => {
        pendingResolveRef.current?.(null);
        pendingResolveRef.current = null;
      },
      [],
    );

    // Surface the active mutation's pending state to a container rendering
    // its own affordance alongside the inline Save button.
    useEffect(() => {
      onSubmittingChange?.(isPending);
    }, [isPending, onSubmittingChange]);

    return (
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <FormField label={t("form.holder")}>
          <input
            type="text"
            value={isEdit ? (credential?.holder_name ?? "") : fixedHolder.name}
            disabled
            readOnly
            className={inputClass}
          />
        </FormField>
        <FormField label={t("form.activityScope")} required error={errors.activity_scope}>
          <Select
            data-field="activity_scope"
            value={values.activity_scope}
            placeholder={t("list.filterScope")}
            options={CREDENTIAL_ACTIVITY_SCOPES.map((s) => ({ value: s, label: t(`scope.${s}`) }))}
            onChange={(e) =>
              // Narrowing cast: the option values are exactly the
              // CredentialActivityScope enum members CREDENTIAL_ACTIVITY_SCOPES
              // carries.
              update("activity_scope", e.target.value as CredentialActivityScope)
            }
          />
        </FormField>
        <FormField label={t("form.certifyingBody")} required error={errors.certifying_body}>
          <input
            data-field="certifying_body"
            type="text"
            value={values.certifying_body}
            onChange={(e) => update("certifying_body", e.target.value)}
            className={inputClass}
          />
        </FormField>
        <FormField
          label={t("form.identificationNumber")}
          required
          error={errors.identification_number}
        >
          <input
            data-field="identification_number"
            type="text"
            value={values.identification_number}
            onChange={(e) => update("identification_number", e.target.value)}
            className={inputClass}
          />
        </FormField>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label={t("form.issuedAt")}>
            <input
              type="date"
              value={values.issued_at}
              onChange={(e) => update("issued_at", e.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label={t("form.expiresAt")} required error={errors.expires_at}>
            <input
              data-field="expires_at"
              type="date"
              value={values.expires_at}
              onChange={(e) => update("expires_at", e.target.value)}
              className={inputClass}
            />
          </FormField>
        </div>
        <FormField label={t("form.notes")}>
          <textarea
            rows={3}
            value={values.notes}
            onChange={(e) => update("notes", e.target.value)}
            className={inputClass}
          />
        </FormField>

        {submitError && (
          <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">{submitError}</div>
        )}

        {!hideActions && (
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onCancel}>
              {t("form.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("form.submit")}
            </Button>
          </div>
        )}
      </form>
    );
  },
);
